// Adaptadores puros: convertem os payloads do backend unificado nos modelos
// renderizáveis da Home (`HomeSection`/`HomeGame`). Ficam separados do componente
// para serem testáveis sem montar React/Swiper/Antd.

import type {
  EdicaoComDimensao,
  EdicaoResumo,
  HomeGame,
  HomeResponse,
  HomeSection,
  PassatempoDoDia,
  TorneioListItem,
} from './types';

/** Ids estáveis das faixas (usados pelo handler de comentários e como key). */
export const SECTION_ID = {
  jogosDoDia: 1,
  cruzadas: 2,
  miniCruzadas: 3,
  edicoes: 4,
} as const;

/** Blinda contra payload fora do esperado (objeto de erro, null, etc.). */
const asArray = <T>(v: T[] | undefined | null): T[] => (Array.isArray(v) ? v : []);

/** Junta pedaços não-vazios com um separador (subtítulo + dimensão). */
const join = (parts: (string | null | undefined)[], sep: string): string =>
  parts.map((p) => p?.trim()).filter(Boolean).join(sep);

function passatempoToGame(p: PassatempoDoDia): HomeGame {
  return {
    id_jogo: p.id,
    id_dificuldade: 0,
    titulo: p.titulo ?? 'Passatempo',
    subtitulo: p.subtitulo ?? '',
    created_at: p.doDia,
    id_tipo: p.tiposDePassatemposId ?? undefined,
  };
}

function edicaoComDimensaoToGame(e: EdicaoComDimensao): HomeGame {
  return {
    id_jogo: e.id,
    id_dificuldade: e.dificuldadesId ?? 0,
    titulo: e.nome ?? 'Edição',
    subtitulo: join([e.subtitulo, e.dimensao], ' · '),
    created_at: e.dataPublicacao,
    // sem id_tipo → card de vitrine (edição não é jogável pela rota atual)
  };
}

function edicaoResumoToGame(e: EdicaoResumo): HomeGame {
  return {
    id_jogo: e.id,
    id_dificuldade: 0,
    titulo: e.nome ?? 'Edição',
    subtitulo: e.subtitulo ?? '',
    created_at: e.dataPublicacao,
  };
}

/**
 * Converte o objeto agregado de `GET /home` nas faixas da Home, na ordem de
 * exibição. Faixas vazias são omitidas. Só "Jogos do Dia" é jogável; as demais
 * (coleções/edições) são vitrines.
 */
export function buildHomeSections(home: HomeResponse | null | undefined): HomeSection[] {
  if (!home) return [];
  const sections: HomeSection[] = [
    {
      id: SECTION_ID.jogosDoDia,
      anchor: 'jogos-do-dia',
      name: 'Jogos do Dia',
      showcase: false,
      jogos: asArray(home.passatemposDoDia).map(passatempoToGame),
    },
    {
      id: SECTION_ID.cruzadas,
      anchor: 'cruzadas',
      name: 'Cruzadas',
      showcase: true,
      jogos: asArray(home.edicoesNormais).map(edicaoComDimensaoToGame),
    },
    {
      id: SECTION_ID.miniCruzadas,
      anchor: 'mini-cruzadas',
      name: 'Mini Cruzadas',
      showcase: true,
      jogos: asArray(home.edicoesMini).map(edicaoComDimensaoToGame),
    },
    {
      id: SECTION_ID.edicoes,
      anchor: 'edicoes',
      name: 'Edições',
      showcase: true,
      jogos: asArray(home.edicoes).map(edicaoResumoToGame),
    },
  ];
  return sections.filter((s) => s.jogos.length > 0);
}

/** Converte a lista de `GET /jogo/torneios` em cards (navegam para /tournaments/list). */
export function buildTournamentGames(data: TorneioListItem[] | null | undefined): HomeGame[] {
  return asArray(data).map((t) => ({
    id_jogo: t.id,
    id_dificuldade: t.dificuldadesId ?? 0,
    titulo: t.nome ?? 'Torneio',
    subtitulo: t.subtitulo ?? '',
    created_at: t.dataPublicacao,
  }));
}
